import { Logo } from "@/components/comiq/Logo";
import { Eyebrow } from "@/components/comiq/Eyebrow";
import { StatusBadge } from "@/components/comiq/StatusBadge";
import { COLORS, CHART_COLORS } from "@/comiq/tokens";
import {
  LayoutDashboard, FileText, Users, Layers, BarChart3, Search,
  Filter, Send, ChevronDown, AlertTriangle, TrendingUp,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Cell,
} from "recharts";

const nav = [
  { icon: LayoutDashboard, label: "Dashboard", active: true },
  { icon: FileText, label: "Instrumen" },
  { icon: Users, label: "Responden" },
  { icon: Layers, label: "Gelombang" },
  { icon: BarChart3, label: "Laporan" },
];

const kpis = [
  { label: "Total responden", value: "1.284", delta: "+8,2%", good: true },
  { label: "Response rate", value: "73%", delta: "+3 pts", good: true },
  { label: "Gelombang aktif", value: "2026 · W2", delta: "12 org", good: null },
  { label: "Flag kualitas data", value: "17", delta: "1,3%", good: false },
];

const responseTrend = [
  { d: "Sen", v: 120 }, { d: "Sel", v: 180 }, { d: "Rab", v: 240 },
  { d: "Kam", v: 210 }, { d: "Jum", v: 320 }, { d: "Sab", v: 140 }, { d: "Min", v: 90 },
];

const byDomain = [
  { name: "Commercial DNA", v: 92 },
  { name: "Intelligence", v: 78 },
  { name: "Behaviour", v: 85 },
  { name: "Capability", v: 70 },
];

const rows = [
  { id: "RSP-4821", org: "PT Nusantara Commercial", wave: "2026 · W2", status: "complete", progress: 100, flag: null, last: "2 jam lalu" },
  { id: "RSP-4822", org: "Garuda Retail Group", wave: "2026 · W2", status: "progress", progress: 62, flag: null, last: "18 mnt lalu" },
  { id: "RSP-4823", org: "Bank Meridian", wave: "2026 · W2", status: "flagged", progress: 100, flag: "Waktu isi terlalu cepat", last: "1 jam lalu" },
  { id: "RSP-4824", org: "Sinar Telco", wave: "2026 · W2", status: "invited", progress: 0, flag: null, last: "—" },
  { id: "RSP-4825", org: "Garuda Retail Group", wave: "2026 · W2", status: "expired", progress: 34, flag: "Tautan kedaluwarsa", last: "3 hari lalu" },
];

const Select = ({ label, value }) => (
  <button
    className="inline-flex items-center gap-2 rounded-comiq-sm border bg-comiq-surface-alt px-3 py-2 text-sm"
    style={{ borderColor: COLORS.line, color: COLORS.ink }}
  >
    <span style={{ color: COLORS.textSoft }}>{label}:</span>
    <span className="font-medium">{value}</span>
    <ChevronDown size={15} style={{ color: COLORS.textSoft }} />
  </button>
);

const chartTooltip = {
  contentStyle: {
    background: "#FFFFFF", border: `1px solid ${COLORS.line}`,
    borderRadius: 10, fontSize: 12, color: COLORS.ink,
  },
};

export default function AdminDashboard() {
  return (
    <div className="flex min-h-screen" style={{ background: COLORS.paper }}>
      {/* Sidebar */}
      <aside className="hidden w-60 shrink-0 border-r lg:flex lg:flex-col" style={{ borderColor: COLORS.line, background: COLORS.surfaceAlt }}>
        <div className="border-b px-5 py-4" style={{ borderColor: COLORS.line }}>
          <Logo />
        </div>
        <nav className="flex-1 p-3">
          {nav.map((n) => (
            <button
              key={n.label}
              data-testid={`nav-${n.label}`}
              className="mb-1 flex w-full items-center gap-3 rounded-comiq-sm px-3 py-2.5 text-sm font-medium transition-colors"
              style={{
                color: n.active ? COLORS.ink : COLORS.textSoft,
                background: n.active ? "rgba(242,84,27,0.08)" : "transparent",
                boxShadow: n.active ? `inset 2px 0 0 ${COLORS.orange}` : "none",
              }}
            >
              <n.icon size={17} style={{ color: n.active ? COLORS.orangeInk : COLORS.textSoft }} />
              {n.label}
            </button>
          ))}
        </nav>
        <div className="border-t p-4" style={{ borderColor: COLORS.line }}>
          <div className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold text-white" style={{ background: COLORS.ink }}>RM</span>
            <div className="text-xs">
              <div className="font-semibold" style={{ color: COLORS.ink }}>Rizky M.</div>
              <div style={{ color: COLORS.textSoft }}>Research Admin · Google</div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 overflow-x-hidden">
        <header className="flex items-center justify-between border-b px-6 py-4" style={{ borderColor: COLORS.line, background: COLORS.surfaceAlt }}>
          <div>
            <Eyebrow>Research Admin</Eyebrow>
            <h1 className="font-serif-editorial text-2xl" style={{ color: COLORS.ink }}>Dashboard gelombang</h1>
          </div>
          <button className="btn-comiq btn-accent" data-testid="invite-btn">
            <Send size={15} /> Kirim undangan
          </button>
        </header>

        <div className="space-y-6 p-6">
          {/* KPIs */}
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {kpis.map((k) => (
              <div key={k.label} data-testid={`kpi-${k.label}`} className="rounded-comiq border bg-comiq-surface-alt p-5" style={{ borderColor: COLORS.line }}>
                <div className="text-xs font-medium" style={{ color: COLORS.textSoft }}>{k.label}</div>
                <div className="mt-2 text-2xl font-bold" style={{ color: COLORS.ink }}>{k.value}</div>
                <div
                  className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold"
                  style={{ color: k.good === false ? "#B42318" : k.good === true ? "#15803D" : COLORS.textSoft }}
                >
                  {k.good === false ? <AlertTriangle size={12} /> : k.good === true ? <TrendingUp size={12} /> : null}
                  {k.delta}
                </div>
              </div>
            ))}
          </div>

          {/* Charts */}
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-comiq border bg-comiq-surface-alt p-5" style={{ borderColor: COLORS.line }}>
              <h3 className="text-sm font-semibold" style={{ color: COLORS.ink }}>Respons per hari</h3>
              <div className="mt-4 h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={responseTrend} margin={{ left: -18, right: 8, top: 6 }}>
                    <CartesianGrid strokeDasharray="3 4" stroke={COLORS.line} vertical={false} />
                    <XAxis dataKey="d" tick={{ fontSize: 11, fill: COLORS.textSoft }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: COLORS.textSoft }} axisLine={false} tickLine={false} />
                    <Tooltip {...chartTooltip} />
                    <Line type="monotone" dataKey="v" stroke={COLORS.orange} strokeWidth={2.5} dot={{ r: 3, fill: COLORS.orange }} activeDot={{ r: 5 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="rounded-comiq border bg-comiq-surface-alt p-5" style={{ borderColor: COLORS.line }}>
              <h3 className="text-sm font-semibold" style={{ color: COLORS.ink }}>Rata-rata penyelesaian per domain (%)</h3>
              <div className="mt-4 h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={byDomain} margin={{ left: -18, right: 8, top: 6 }}>
                    <CartesianGrid strokeDasharray="3 4" stroke={COLORS.line} vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 10, fill: COLORS.textSoft }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: COLORS.textSoft }} axisLine={false} tickLine={false} />
                    <Tooltip {...chartTooltip} cursor={{ fill: "rgba(23,20,15,0.04)" }} />
                    <Bar dataKey="v" radius={[6, 6, 0, 0]}>
                      {byDomain.map((_, i) => (
                        <Cell key={i} fill={i === 0 ? COLORS.orange : CHART_COLORS[(i % (CHART_COLORS.length - 1)) + 1]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 text-sm font-medium" style={{ color: COLORS.textSoft }}>
              <Filter size={15} /> Filter
            </div>
            <Select label="Gelombang" value="2026 · W2" />
            <Select label="Organisasi" value="Semua" />
            <Select label="Status" value="Semua" />
            <div className="ml-auto flex items-center gap-2 rounded-comiq-sm border bg-comiq-surface-alt px-3 py-2" style={{ borderColor: COLORS.line }}>
              <Search size={15} style={{ color: COLORS.textSoft }} />
              <input
                data-testid="search-input"
                placeholder="Cari ID responden…"
                className="w-40 bg-transparent text-sm outline-none"
                style={{ color: COLORS.ink }}
              />
            </div>
          </div>

          {/* Table */}
          <div className="overflow-hidden rounded-comiq border bg-comiq-surface-alt" style={{ borderColor: COLORS.line }}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="respondents-table">
                <thead>
                  <tr style={{ background: COLORS.surface, color: COLORS.textSoft }}>
                    {["ID Responden", "Organisasi", "Gelombang", "Status", "Progres", "Kualitas data", "Aktivitas"].map((h) => (
                      <th key={h} className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-t" style={{ borderColor: COLORS.line }}>
                      <td className="px-4 py-3 font-mono text-xs font-semibold" style={{ color: COLORS.ink }}>{r.id}</td>
                      <td className="whitespace-nowrap px-4 py-3" style={{ color: COLORS.ink }}>{r.org}</td>
                      <td className="whitespace-nowrap px-4 py-3" style={{ color: COLORS.textSoft }}>{r.wave}</td>
                      <td className="px-4 py-3"><StatusBadge kind={r.status} testId={`status-${r.id}`} /></td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="h-1.5 w-20 overflow-hidden rounded-full" style={{ background: COLORS.line }}>
                            <div className="h-full rounded-full" style={{ width: `${r.progress}%`, background: r.progress === 100 ? "#15803D" : COLORS.orange }} />
                          </div>
                          <span className="text-xs" style={{ color: COLORS.textSoft }}>{r.progress}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {r.flag ? (
                          <span className="inline-flex items-center gap-1.5 text-xs font-medium" style={{ color: "#B42318" }}>
                            <AlertTriangle size={13} /> {r.flag}
                          </span>
                        ) : (
                          <span className="text-xs" style={{ color: COLORS.textSoft }}>—</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-xs" style={{ color: COLORS.textSoft }}>{r.last}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
